/** Music theory primitives. Pure functions, no state. */

export const NOTE_NAMES = [
  'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B',
] as const;

export interface Mode {
  name: string;
  /** semitone offsets from the root */
  semis: number[];
}

/** Ordered bright -> dark; the temperature ladder indexes into this (02_..._mapping.md §7). */
export const MODES: Mode[] = [
  { name: 'Lydian', semis: [0, 2, 4, 6, 7, 9, 11] },
  { name: 'Ionian', semis: [0, 2, 4, 5, 7, 9, 11] },
  { name: 'Mixolydian', semis: [0, 2, 4, 5, 7, 9, 10] },
  { name: 'Dorian', semis: [0, 2, 3, 5, 7, 9, 10] },
  { name: 'Aeolian', semis: [0, 2, 3, 5, 7, 8, 10] },
  { name: 'Phrygian', semis: [0, 1, 3, 5, 7, 8, 10] },
  { name: 'Locrian', semis: [0, 1, 3, 5, 6, 8, 10] },
];

const mod12 = (n: number): number => ((n % 12) + 12) % 12;

export function midiName(m: number): string {
  return NOTE_NAMES[mod12(m)] + (Math.floor(m / 12) - 1);
}

export function midiToFreq(m: number): number {
  return 440 * Math.pow(2, (m - 69) / 12);
}

/** All MIDI notes of (root + mode) within [lo, hi], ascending. */
export function scalePitches(rootPc: number, semis: number[], lo: number, hi: number): number[] {
  const pcs = new Set(semis.map((s) => mod12(rootPc + s)));
  const out: number[] = [];
  for (let m = lo; m <= hi; m++) if (pcs.has(mod12(m))) out.push(m);
  return out;
}

/** Warmer => brighter mode (lower index). Returns an index into MODES. */
export function modeIndexForTemp(tempF: number): number {
  if (tempF >= 82) return 0; // Lydian
  if (tempF >= 70) return 1; // Ionian (default anchor)
  if (tempF >= 58) return 2; // Mixolydian
  if (tempF >= 45) return 3; // Dorian
  if (tempF >= 32) return 4; // Aeolian
  if (tempF >= 18) return 5; // Phrygian
  return 6; //               Locrian
}
